import { useEffect, useState } from "react"
import _forEach from "lodash/forEach"
import _map from "lodash/map"
import _round from "lodash/round"
import Bugsnag from "@bugsnag/js"

import useGtm from "@src/hooks/use-gtm"
import { useAppDispatch, useAppSelector } from "@src/hooks/use-redux"
import useStoryblokBridge from "@src/hooks/use-storyblok-bridge"

import getGlobalSiteSettings from "@src/pages/api/get-global-site-settings"
import getPaths from "@src/pages/api/get-product-paths"

import { GET_PRODUCT } from "@src/operations/api/product"

import { setCartCount } from "@src/redux/cart.slice"
import { getUser } from "@src/redux/user.slice"

import apolloClient from "@src/utilities/apollo-client"
import { maybeScrubPropsForCleanSite } from "@src/utilities/clean-site"
import siteUrl from "@src/utilities/get-site-url"
import Storyblok from "@src/utilities/storyblok"

import getStoryblokApiKey from "@src/utilities/storyblok-api-key"
import populateCards from "@src/utilities/populate-cards"

import { addProductToCart } from "@src/workers/api/add-to-cart"
import { getProductPriceStock } from "@src/workers/api/get-prices-and-stock"
import getStoryblokCacheVersion from "@src/workers/storyblok/get-cache-version"

import GeneralLayout from "@src/layouts/general"
import PageBuilder from "@src/components/page-builder"
import ProductHeader from "@src/components/ProductHeader"
import SEO from "@src/components/seo"

import type { GetStaticPropsContext, InferGetStaticPropsType } from "next"
import type {
  GetProductQuery,
  GetProductQueryVariables,
} from "@src/entities/types/api"
import type { CartItemPurchaseType } from "@src/entities/types/cart"
import type { SiteSettings } from "@src/entities/types/menu"

export default function ProductPage({
  preview,
  product,
  siteSettings,
  story,
  storyblokApiKey,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(getUser)
  const blok = useStoryblokBridge(story, storyblokApiKey, preview)

  const [addingToBag, setAddingToBag] = useState<boolean>(false)
  const [frequencyInWeeks, setFrequencyInWeeks] = useState<number>(4)
  const [isInStock, setIsInStock] = useState<boolean>(product.stockLevel > 0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [quantity, setQuantity] = useState<number>(1)
  const [retailPrice, setRetailPrice] = useState<number>(product.priceRetail)
  const [salePrice, setSalePrice] = useState<number | null>(product.priceOnSale)
  const [selectedPurchaseType, setSelectedPurchaseType] =
    useState<CartItemPurchaseType>("one-time")
  const [subscriptionPrice, setSubscriptionPrice] = useState<number | null>(
    product.priceSubscription
  )
  const [wholesalePrice, setWholesalePrice] = useState<number | null>(
    product.priceWholesale
  )

  const changeSelectedPurchaseType = (newType: CartItemPurchaseType) => {
    setSelectedPurchaseType((prevState) => {
      if (newType !== prevState) {
        useGtm({
          event: "orTrack",
          eventName: "Product Viewed",
          properties: {
            ...(product.categoryName && { category: product.categoryName }),
            ...(selectedPurchaseType === "subscription" && {
              frequencyInWeeks,
            }),
            image_url: product.thumbnailUrl,
            name: product?.name,
            price: _round(
              0.01 *
                (selectedPurchaseType === "subscription"
                  ? product.priceSubscription
                  : product.priceOnSale ?? product.priceRetail),
              2
            ),
            product_id: product?.sku,
            quantity,
            sku: product?.sku,
            ...(product.description && { variant: product.description }),
            value: _round(
              quantity *
                0.01 *
                (selectedPurchaseType === "subscription"
                  ? product.priceSubscription
                  : product.priceOnSale ?? product.priceRetail),
              2
            ),
          },
        })
      }
      return newType
    })
  }

  const getPriceStock = async (sku: string) => {
    const { data } = await getProductPriceStock(sku)
    if (data) {
      setIsInStock(data.product.stock !== 0)
      setRetailPrice(data.product.priceRetail)
      setSalePrice(data.product.priceOnSale)
      setSubscriptionPrice(data.product.priceSubscription)
      setWholesalePrice(data.product.priceWholesale)
      setIsLoading(false)
    }
  }

  const handleAddToCart = async () => {
    setAddingToBag(true)
    const { cartCount, errors } = await addProductToCart({
      ...(selectedPurchaseType === "subscription" && { frequencyInWeeks }),
      sku: product.sku,
      quantity,
    })
    if (errors) {
      _forEach(errors, (error) => {
        Bugsnag.notify(new Error(error.message))
      })
      setAddingToBag(false)
      return
    }
    dispatch(setCartCount(cartCount))
    setAddingToBag(false)
  }

  const onChangeFrequency = (newFrequency: number) => {
    setFrequencyInWeeks((prevState) => {
      if (newFrequency !== prevState) {
        useGtm({
          event: "orTrack",
          eventName: "Product Viewed",
          properties: {
            ...(product.categoryName && { category: product.categoryName }),
            ...(selectedPurchaseType === "subscription" && {
              frequencyInWeeks,
            }),
            image_url: product.thumbnailUrl,
            name: product?.name,
            price: _round(
              0.01 *
                (selectedPurchaseType === "subscription"
                  ? product.priceSubscription
                  : product.priceOnSale ?? product.priceRetail),
              2
            ),
            product_id: product?.sku,
            quantity,
            sku: product?.sku,
            ...(product.description && { variant: product.description }),
            value: _round(
              quantity *
                0.01 *
                (selectedPurchaseType === "subscription"
                  ? product.priceSubscription
                  : product.priceOnSale ?? product.priceRetail),
              2
            ),
          },
        })
      }
      return newFrequency
    })
  }

  const onChangeQuantity = (newQuantity: number) => {
    setQuantity((prevState) => {
      if (newQuantity !== prevState) {
        useGtm({
          event: "orTrack",
          eventName: "Product Viewed",
          properties: {
            ...(product.categoryName && { category: product.categoryName }),
            ...(selectedPurchaseType === "subscription" && {
              frequencyInWeeks,
            }),
            image_url: product.thumbnailUrl,
            name: product?.name,
            price: _round(
              0.01 *
                (selectedPurchaseType === "subscription"
                  ? product.priceSubscription
                  : product.priceOnSale ?? product.priceRetail),
              2
            ),
            product_id: product?.sku,
            quantity: newQuantity,
            sku: product?.sku,
            ...(product.description && { variant: product.description }),
            value: _round(
              newQuantity *
                0.01 *
                (selectedPurchaseType === "subscription"
                  ? product.priceSubscription
                  : product.priceOnSale ?? product.priceRetail),
              2
            ),
          },
        })
      }
      return newQuantity
    })
  }

  useEffect(() => {
    if (product) {
      getPriceStock(product.sku)
    }
  }, [product])

  useEffect(() => {
    onChangeQuantity(quantity)
  }, [quantity])

  useEffect(() => {
    if (user) {
      useGtm({
        event: "orTrack",
        eventName: "Product Viewed",
        properties: {
          ...(product.categoryName && { category: product.categoryName }),
          ...(selectedPurchaseType === "subscription" && {
            frequencyInWeeks,
          }),
          image_url: product.thumbnailUrl,
          name: product?.name,
          price: _round(
            0.01 *
              (selectedPurchaseType === "subscription"
                ? product.priceSubscription
                : product.priceOnSale ?? product.priceRetail),
            2
          ),
          product_id: product?.sku,
          quantity,
          sku: product?.sku,
          ...(product.description && { variant: product.description }),
          value: _round(
            quantity *
              0.01 *
              (selectedPurchaseType === "subscription"
                ? product.priceSubscription
                : product.priceOnSale ?? product.priceRetail),
            2
          ),
        },
      })
    }
  }, [user])

  return (
    <GeneralLayout siteSettings={siteSettings}>
      {blok.content && blok.content.seo && <SEO {...blok.content.seo} />}
      <ProductHeader
        amazonUrl={blok.content?.amazon_link?.url || null}
        featuredImages={
          blok.content?.featured_images?.map((image: any) => image.filename) ??
          []
        }
        inStock={isInStock}
        isAddingToBag={addingToBag}
        isReady={user.authStateKnown && !isLoading}
        minimumQuantityForSubscription={1}
        onAddToCart={handleAddToCart}
        onChangeFrequency={onChangeFrequency}
        onChangeSelectedPurchaseType={changeSelectedPurchaseType}
        onChangeQuantity={setQuantity}
        originalRetailPrice={retailPrice}
        oneTimePurchasePrice={salePrice}
        productIconLinks={blok?.content?.product_links}
        productLongDescription={Storyblok().richTextResolver.render(
          blok.content.product_long_description
        )}
        productName={product!.name}
        productPageUrl={blok.full_slug}
        productShortDescription={product!.description}
        productSku={product!.sku}
        selectedFrequencyInWeeks={frequencyInWeeks}
        selectedQuantity={quantity}
        selectedPurchaseType={selectedPurchaseType}
        subscriptionPurchasePrice={Math.min(
          subscriptionPrice ?? retailPrice,
          salePrice ?? retailPrice
        )}
        wholesalePurchasePrice={wholesalePrice}
      />
      {blok &&
        blok.content &&
        blok.content.page_builder &&
        blok.content.page_builder.map((body: any) => (
          <PageBuilder content={body} key={body._uid} />
        ))}
      <div className="container-tablet" id="reviews">
        <h2 className="font-bold mb-2 text-2xl lg:text-3xl">
          What People Are Saying...
        </h2>
        <p className="prose prose-lg max-w-none mb-4">
          The following reviews were created by verified purchasers of Onyx +
          Rose products. These statements have not been evaluated by the FDA.
          Product is not intended to diagnose, treat, cure or prevent any
          disease.
        </p>
        <div
          className="yotpo yotpo-main-widget"
          data-product-id={product?.sku}
          data-price={Math.floor(0.01 * product?.priceRetail).toFixed(2)}
          data-currency="USD"
          data-name={product?.name}
          data-url={`${siteUrl()}/${blok.full_slug}`}
          data-image-url={product?.thumbnailUrl ?? ""}
        ></div>
      </div>
    </GeneralLayout>
  )
}

export const getStaticPaths = async () => {
  const paths = await getPaths()
  return {
    paths: _map(paths, (slug) => ({
      params: {
        slug: slug.split("/").slice(1),
      },
    })),
    fallback: false,
  }
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const slugs = (context.params && (context.params.slug as string[])) ?? []
  const apiKey = getStoryblokApiKey(context.preview)
  const cacheVersion = await getStoryblokCacheVersion()

  const [siteSettings, storyRequest] = await Promise.all([
    getGlobalSiteSettings(context.preview ?? false),
    Storyblok(apiKey).get(`cdn/stories/product/${slugs.join("/")}`, {
      cv: cacheVersion,
      version: context.preview ? "draft" : "published",
    }),
  ])

  const sku: string =
    storyRequest?.data?.story?.content?.sanity_product?.sku ?? "NOSKU"

  const product = await apolloClient
    .query<GetProductQuery, GetProductQueryVariables>({
      query: GET_PRODUCT,
      variables: {
        sku,
      },
    })
    .then(({ data }) => data?.product)

  const story = await populateCards(storyRequest.data.story)

  return {
    notFound: !product,
    props: {
      preview: context.preview ?? false,
      product: {
        categoryName: product?.categoryName ?? null,
        description: product?.description ?? null,
        name: product?.name ?? null,
        priceRetail: product?.priceRetail ?? 0,
        priceOnSale:
          product?.priceOnSale && product?.priceOnSale > 0
            ? product?.priceOnSale
            : null,
        priceSubscription:
          product?.priceSubscription && product?.priceSubscription > 0
            ? product?.priceSubscription
            : null,
        priceWholesale:
          product?.priceWholesale && product?.priceWholesale > 0
            ? product.priceWholesale
            : null,
        preview: context.preview ?? false,
        sku,
        stockLevel: product?.stock,
        thumbnailUrl: product?.thumbnailUrl,
      },
      oldProduct:
        product && maybeScrubPropsForCleanSite<typeof product>(product),
      siteSettings: maybeScrubPropsForCleanSite<SiteSettings>(siteSettings),
      story,
      ...(context.preview && { storyblokApiKey: apiKey }),
    },
  }
}
